/**
 * /shorts/proposer — Formulaire de soumission d'un Short par un membre.
 *
 * Fonctionnement :
 *  - Visiteurs non connectés : lecture seule du bloc "Comment ça marche" + boutons
 *    qui ouvrent le dropdown Connexion (pas de redirection sèche).
 *  - Membres connectés : formulaire actif.
 *  - Soumission par lien externe (YouTube / Vimeo / Dailymotion / TikTok / Instagram).
 *  - La case CGU est obligatoire (bloquante côté client + serveur).
 *  - Après succès, affichage du statut "en attente de modération".
 */
import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, Lock, PlayCircle, Send, Upload, ShieldCheck } from 'lucide-react';
import api from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import useSeo from '../hooks/useSeo';

const THEMES = [
  { v: 'equipement', l: 'Équipement' },
  { v: 'etm', l: 'ETM (interrogations orales)' },
  { v: 'plateau', l: 'Plateau' },
  { v: 'circulation', l: 'Circulation' },
  { v: 'trajectoires', l: 'Trajectoires de sécurité' },
  { v: 'freinage', l: 'Freinage' },
  { v: 'angles-morts', l: 'Angles morts & visibilité' },
  { v: 'interfiles', l: 'Interfiles' },
  { v: 'meteo', l: 'Météo & conditions' },
  { v: 'mentale', l: 'Préparation mentale' },
  { v: 'gestes-signaleur', l: 'Gestes du signaleur' },
  { v: 'autre', l: 'Autre' },
];

export default function ShortsProposerPage() {
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [mine, setMine] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [theme, setTheme] = useState('');
  const [url, setUrl] = useState('');
  const [cgu, setCgu] = useState(false);
  const [cguFlash, setCguFlash] = useState(false); // flash rouge temporaire si l'élève clique "Envoyer" sans cocher
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const cguRef = useRef(null);

  useSeo({
    title: 'Proposer un Short — Code 2 Roues',
    description:
      'Partagez votre meilleure astuce moto en vidéo verticale (lien YouTube, Vimeo, TikTok…). Modération sous 48 h.',
    canonical: 'https://code2roues.fr/shorts/proposer',
  });

  useEffect(() => {
    (async () => {
      try {
        const u = await api.getMe();
        setUser(u);
        const list = await api.shortsMine();
        setMine(list || []);
      } catch { /* non connecté — on reste en lecture seule */ }
      setLoadingUser(false);
    })();
  }, []);

  // Redirige vers la page de connexion élève en conservant l'URL de retour
  // afin que l'utilisateur revienne automatiquement sur /shorts/proposer
  // après authentification. Fonctionne desktop + mobile (plus de hack
  // document.querySelector sur un testid qui n'existe plus).
  const loginHref = `/login?role=eleve&next=${encodeURIComponent('/shorts/proposer')}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validation CGU avec feedback visuel immédiat :
    //  - toast rouge explicite + bouton "Cocher la case automatiquement" dans la toast
    //  - scroll smooth vers la case (critique sur mobile où elle peut être hors écran)
    //  - flash rouge animé pendant 1.6 s pour attirer l'œil
    if (!cgu) {
      // iOS Safari : utilise toast action pour permettre à l'élève de cocher
      // directement depuis la notification, sans scroller.
      toast.error('⚠️ Cochez la case des conditions pour envoyer', {
        duration: 6000,
        action: {
          label: 'Cocher pour moi',
          onClick: () => {
            setCgu(true);
            toast.success('✅ Case cochée — vous pouvez maintenant envoyer.');
          },
        },
      });
      try {
        cguRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch { /* no-op */ }
      setCguFlash(true);
      setTimeout(() => setCguFlash(false), 1600);
      return;
    }
    if (!theme) { toast.error('Choisissez un thème.'); return; }
    if (title.trim().length < 4) { toast.error('Titre trop court (4 car. min).'); return; }
    if (!url.trim()) { toast.error('Collez le lien de votre vidéo.'); return; }
    setSubmitting(true);
    try {
      const res = await api.shortsSubmit({
        title: title.trim(),
        description: description.trim(),
        theme,
        url: url.trim(),
        cgu_accepted: true,
      });
      setSuccess(res);
      toast.success('Proposition envoyée — notre équipe va la relire sous 48 h.');
      setTitle(''); setDescription(''); setTheme(''); setUrl(''); setCgu(false);
      const list = await api.shortsMine().catch(() => []);
      setMine(list || []);
    } catch (e2) {
      toast.error(e2.message || 'Impossible d\'envoyer la proposition.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="shorts-proposer-page">
      <div className="mb-8">
        <Link to="/shorts" className="text-xs uppercase tracking-widest text-[hsl(var(--primary))] font-black">
          ← Retour aux Shorts
        </Link>
        <h1 className="text-3xl md:text-4xl font-extrabold mt-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Proposer un Short
        </h1>
        <p className="text-foreground/70 mt-2">
          Partagez votre meilleure astuce moto, un retour d'expérience, un plateau réussi,
          un geste de signaleur à expliquer… Les propositions sont relues sous 48 h.
        </p>
      </div>

      {/* ======== ANNONCE DE LANCEMENT (Oct 2026) ========
          Rassure l'élève sur le processus : contribution libre, validation éditoriale,
          respect des droits. Reprend le texte officiel communiqué cross-site. */}
      <section
        className="relative rounded-2xl border border-rose-500/30 bg-gradient-to-br from-rose-950/40 via-black/70 to-black p-5 md:p-6 mb-8 overflow-hidden"
        data-testid="shorts-proposer-launch-msg"
        aria-labelledby="launch-msg-title"
      >
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-rose-300 mb-2">
            🚀 Nouveau — Lancement communauté
          </div>
          <h2
            id="launch-msg-title"
            className="text-white text-[20px] md:text-[22px] font-black leading-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Vos Shorts arrivent sur Code 2 Roues &amp; CodeVoiture.fr
          </h2>
          <div className="mt-3 space-y-2.5 text-[13.5px] text-white/80 leading-relaxed">
            <p>
              Vous apprenez à conduire ? Vous avez une <strong className="text-white">astuce, une
              expérience ou une vidéo</strong> qui pourrait aider d'autres élèves ? Partagez-la.
            </p>
            <p className="text-white/70">
              🎬 Une manœuvre qui vous a fait progresser, une astuce pour réussir un
              créneau, un conseil pour aborder un giratoire, un exercice de plateau moto
              ou une erreur à éviter — chaque expérience peut être utile à quelqu'un
              d'autre.
            </p>
          </div>

          <div className="mt-4 grid sm:grid-cols-3 gap-2.5 text-[12.5px]">
            <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
              <div className="text-[10px] font-black uppercase tracking-wider text-rose-300 mb-1">1. Proposez</div>
              <p className="text-white/70 leading-snug">
                Un lien <strong className="text-white">TikTok</strong>, YouTube, Vimeo ou
                une vidéo pertinente sur la conduite moto / auto.
              </p>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
              <div className="text-[10px] font-black uppercase tracking-wider text-rose-300 mb-1">2. Expliquez</div>
              <p className="text-white/70 leading-snug">
                Ajoutez quelques mots pour dire <strong className="text-white">ce qu'elle vous a appris</strong> ou pourquoi elle aide.
              </p>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
              <div className="text-[10px] font-black uppercase tracking-wider text-rose-300 mb-1">3. On valorise</div>
              <p className="text-white/70 leading-snug">
                Après vérification, votre sélection peut être mise en avant sur <strong className="text-white">Code 2 Roues</strong> (moto) ou <strong className="text-white">CodeVoiture.fr</strong> (auto).
              </p>
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-[12px] text-amber-200/90 leading-snug">
            <strong className="text-amber-300">⚠️ Important :</strong> proposer le lien d'une
            vidéo (TikTok, YouTube, autre) n'équivaut <strong>pas</strong> à une autorisation
            automatique de republication. L'équipe éditoriale vérifie les droits et conditions
            d'intégration de chaque plateforme avant toute mise en avant. Si vous souhaitez
            soumettre votre <strong>propre vidéo</strong>, une autorisation de diffusion
            adaptée pourra vous être demandée.
          </div>
        </div>
      </section>

      {/* Bloc "Comment ça marche" en 3 étapes */}
      <div className="grid md:grid-cols-3 gap-3 mb-10">
        {[
          { icon: Send, title: '1. Tu proposes', body: 'Colle un lien YouTube / Vimeo / TikTok ou téléverse un MP4 vertical (bientôt).' },
          { icon: ShieldCheck, title: '2. On valide', body: 'Notre équipe vérifie le contenu (droits, pertinence pédagogique, sécurité).' },
          { icon: PlayCircle, title: '3. C\'est en ligne', body: 'Dès validation, ton short apparaît dans le fil public et compte les vues.' },
        ].map((s, i) => (
          <div key={i} className="rounded-xl border border-border/50 bg-card p-4" data-testid={`how-step-${i + 1}`}>
            <s.icon className="w-6 h-6 text-[hsl(var(--primary))] mb-2" />
            <h3 className="font-black text-sm uppercase tracking-wider">{s.title}</h3>
            <p className="text-xs text-foreground/65 mt-1 leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>

      {/* État "non connecté" */}
      {!loadingUser && !user && (
        <Card className="border-dashed">
          <CardContent className="py-10 text-center">
            <Lock className="w-10 h-10 mx-auto text-foreground/40" />
            <h3 className="font-extrabold mt-3 text-lg">Connexion requise</h3>
            <p className="text-sm text-foreground/60 mt-2 max-w-md mx-auto">
              Pour proposer un Short, vous devez disposer d'un compte membre Code 2 Roues.
              C'est gratuit et ça prend 30 secondes.
            </p>
            <div className="flex gap-2 justify-center mt-5 flex-wrap">
              <Link to={loginHref}>
                <Button data-testid="proposer-login-btn">Se connecter</Button>
              </Link>
              <Link to="/register"><Button variant="outline" data-testid="proposer-register-btn">Créer un compte</Button></Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Succès — affiché juste après soumission pour rassurer l'élève */}
      {success && (
        <div className="rounded-xl border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 p-5 mb-6 space-y-3" data-testid="shorts-submit-success">
          <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="w-5 h-5" />
            <strong className="text-base">Proposition reçue !</strong>
          </div>
          <p className="text-sm text-emerald-800 dark:text-emerald-200">
            L'équipe admin a été notifiée par email et va relire votre vidéo sous 48 h.
            Vous recevrez un email dès qu'elle sera publiée ou refusée.
          </p>
          <div className="flex items-center gap-2 flex-wrap pt-1">
            <Link
              to="/eleve/mes-shorts"
              data-testid="shorts-submit-success-cta-mine"
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-black uppercase tracking-wider text-[11px] transition"
            >
              <PlayCircle className="w-3.5 h-3.5" />
              Suivre mes propositions
            </Link>
            <button
              type="button"
              onClick={() => setSuccess(null)}
              className="h-9 px-3 rounded-full border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-[11px] font-bold uppercase tracking-wider hover:bg-emerald-500/10 transition"
            >
              Proposer un autre short
            </button>
          </div>
        </div>
      )}

      {/* Formulaire actif pour membre connecté */}
      {user && (
        <Card>
          <CardHeader>
            <CardTitle>Formulaire</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="shorts-proposer-form">
              <div>
                <Label htmlFor="title">Titre * <span className="text-foreground/40 font-normal">(90 car. max)</span></Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={90}
                  placeholder="Ex. : Mon exercice d'évitement en plateau — vue POV"
                  required
                  data-testid="shorts-input-title"
                />
              </div>
              <div>
                <Label htmlFor="theme">Thème *</Label>
                <Select value={theme} onValueChange={setTheme}>
                  <SelectTrigger data-testid="shorts-input-theme"><SelectValue placeholder="Choisir un thème" /></SelectTrigger>
                  <SelectContent>
                    {THEMES.map((t) => (
                      <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="url">Lien de la vidéo *</Label>
                <Input
                  id="url"
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=…"
                  required
                  data-testid="shorts-input-url"
                />
                <p className="text-xs text-foreground/55 mt-1">
                  Formats acceptés : YouTube, Vimeo, Dailymotion, TikTok, Instagram. HTTPS uniquement.
                </p>
              </div>
              <div>
                <Label htmlFor="description">Description <span className="text-foreground/40 font-normal">(300 car. max)</span></Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={300}
                  rows={3}
                  placeholder="En une ou deux phrases : qu'est-ce qu'on apprend en regardant ta vidéo ?"
                  data-testid="shorts-input-description"
                />
              </div>
              <div
                ref={cguRef}
                className={`rounded-lg border-2 p-4 transition-all ${
                  cgu
                    ? 'border-emerald-500/60 bg-emerald-500/10'
                    : cguFlash
                      ? 'border-red-500 bg-red-500/20 shadow-[0_0_0_6px_rgba(239,68,68,0.25)] animate-pulse'
                      : 'border-amber-500/70 bg-amber-500/10 shadow-[0_0_0_3px_rgba(245,158,11,0.12)]'
                }`}
                data-testid="shorts-cgu-wrapper"
              >
                {/* Label avec zone de tap étendue (min 44x44 iOS). Un gros carré
                    cliquable + input visuellement plus grand pour éviter les
                    hésitations sur mobile. */}
                <label className="flex items-start gap-3 cursor-pointer select-none active:bg-black/5 rounded-md -m-1 p-1">
                  <input
                    type="checkbox"
                    checked={cgu}
                    onChange={(e) => setCgu(e.target.checked)}
                    className="mt-0.5 w-6 h-6 accent-rose-600 cursor-pointer flex-shrink-0"
                    data-testid="shorts-input-cgu"
                  />
                  <span className="text-sm text-foreground/90 leading-relaxed">
                    {!cgu && (
                      <span className="block mb-1.5 text-amber-800 dark:text-amber-200 font-black text-[13px] uppercase tracking-wide">
                        ⚠️ Étape 1 : cochez cette case
                      </span>
                    )}
                    Je certifie disposer des droits sur cette vidéo (droits d'auteur et droit à l'image),
                    j'accepte la licence gratuite non exclusive au profit de Code 2 Roues, et je m'engage
                    à respecter l'intégralité des{' '}
                    <Link to="/videos/conditions" className="text-[hsl(var(--primary))] underline">
                      conditions de contribution
                    </Link>. Pour les mineurs : accord parental obligatoire.
                  </span>
                </label>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {/* IMPORTANT iOS : type="button" + onClick direct au lieu de
                    type="submit" via formulaire. Évite les cas où Safari iOS
                    n'intercepte pas le submit (lié aux boutons disabled dans
                    la version précédente). handleSubmit accepte un event
                    partiel (preventDefault no-op). */}
                <Button
                  type="button"
                  onClick={() => handleSubmit({ preventDefault: () => {} })}
                  disabled={submitting}
                  data-testid="shorts-submit-btn"
                  className="h-12 px-6 text-base font-black uppercase tracking-wide w-full sm:w-auto"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  {cgu ? 'Envoyer pour modération' : 'Envoyer (cochez CGU d\'abord)'}
                </Button>
                {!cgu && (
                  <span className="text-[11.5px] text-amber-700 dark:text-amber-300 font-semibold inline-flex items-center gap-1 w-full sm:w-auto">
                    ↑ Appuyez pour voir où cocher
                  </span>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Mes shorts envoyés — avec lien pleine vue vers /eleve/mes-shorts */}
      {user && mine.length > 0 && (
        <Card className="mt-8" data-testid="shorts-mine-card">
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle>Mes shorts envoyés</CardTitle>
            <Link
              to="/eleve/mes-shorts"
              className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider text-[hsl(var(--primary))] hover:underline"
              data-testid="shorts-mine-link-fullview"
            >
              Vue détaillée →
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {mine.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-3 p-3 rounded-md border border-border/50">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">{s.title}</div>
                    <div className="text-[11px] text-foreground/55 mt-0.5">
                      Proposé le {new Date(s.created_at).toLocaleDateString('fr-FR')}
                    </div>
                    {s.reject_reason && (
                      <div className="text-[11px] text-red-500 mt-1">Motif : {s.reject_reason}</div>
                    )}
                  </div>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold uppercase ${
                      s.status === 'published' ? 'bg-emerald-100 text-emerald-700' :
                      s.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}
                  >
                    {s.status === 'published' ? 'Publié' : s.status === 'rejected' ? 'Refusé' : 'En attente'}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-foreground/50 mt-10 text-center">
        En proposant une vidéo, vous confirmez avoir lu et accepté les{' '}
        <Link to="/videos/conditions" className="underline">conditions complètes</Link>.
      </p>
    </div>
  );
}
